import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { IntlProvider, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import './styles/main.css';

// Import pages and components
import Home from './pages/Home';
import EventDetail from './pages/EventDetail';
import PublicProfilePage from './components/publicProfile/PublicProfilePage';
import ProgramPrintPage from './pages/ProgramPrintPage';
import CallForAbstracts from './pages/CallForAbstracts';
import Contact from './pages/Contact';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import ContentManagement from './components/admin/ContentManagement/ContentManagement';
import ResetPassword  from './components/admin/ResetPassword';
import AttendeeLogin from './components/attendee/AttendeeLogin';
import VerifyEmail from './components/attendee/VerifyEmail';
import AttendeeEventRegisterConfirm from './components/AttendeeEventRegisterConfirm';

// The member area lives in src/components/self/ and is on the real routes since the switch (P15):
// /profile, /signup, /signup/success, /speaker/invite, /speaker/invite/respond,
// /speaker/invite/confirmed and /speaker/dashboard. These are the pre-switch screens (the customer's
// "crown jewel" profile and the former Speaker Hub, now the profile's speaker area), moved in from components/attendee and
// components/speaker with their data layer pointed at the new member API (/api/me/**) - the look and
// the copy are the old ones, so a person sees the same page as before the switch.
import { AdminRoute, MemberRoute, SpeakerRoute } from './components/authority/RoleRoute';
import AttendeeProfile from './components/self/profile/AttendeeProfile';
import AttendeeSignup from './components/self/signup/AttendeeSignup';
import SignupSuccess from './components/self/signup/SignupSuccess';
import SpeakerInvitationLanding from './components/self/invite/SpeakerInvitationLanding';
import SpeakerInviteConfirmation from './components/self/invite/SpeakerInviteConfirmation';
import SpeakerInviteRespond from './components/self/invite/SpeakerInviteRespond';

import SubmissionsPage from './components/submissions/SubmissionsPage';
import SubmissionList from './components/admin/SubmissionManagement/SubmissionList';
import ProgramAdmin from './components/programAdmin/ProgramAdminContainer';
import PersonList from './components/personAdmin/PersonList';
import PersonDetail from './components/personAdmin/PersonDetail';
import TeamManagement from './components/staffAdmin/TeamManagement';
import EmailAdminScreen from './components/emailAdmin/EmailAdminScreen';

import Header from './layouts/Header';
import Footer from './layouts/Footer';
import Modal from './components/Modal';
import ImpressumDisplay from './components/ImpressumDisplay';
import ScrollToTop from './components/ScrollToTop';

import CookieConsent from './components/CookieConsent';
import MatomoTracker from './components/MatomoTracker';
import AnalyticsTracker from './components/AnalyticsTracker';

// Import language files
import de from './locales/de.json';
import en from './locales/en.json';

// Import the ConferenceProvider
import { ConferenceProvider } from './contexts/ConferenceContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { getRole, refreshRoles } from './utils/authUtils';

const messages = {
  de: de,
  en: en,
};

// The HEARTS MATCH call-for-abstracts flow (explainer page + submission
// form) targets an international scientific audience and is English-only by
// design - the customer deliberately keeps this copy in English even when
// the site language is German. A nested provider pins these pages to the en
// catalog regardless of the visitor's language choice; the surrounding site
// chrome (header/footer) keeps following the site language.
const EnglishOnly = ({ children }) => (
  <IntlProvider locale="en" messages={en}>
    {children}
  </IntlProvider>
);

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(to bottom, #CAD4DD, #060d25);
`;

const ContentWrapper = styled.main`
  flex: 1;
`;

function AppContent() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // Roles are read from GET /api/me, never from the cache an earlier login left behind: a token
  // refresh carries none, and a browser that logged in before the switch still holds the retired
  // ATTENDEE / SPEAKER names. Three things re-read them: the guards on every mount, the 401 path of
  // the response interceptor (authUtils) after it has refreshed the token, and this start-up sync.
  // The token refresh the request interceptor does *before* a request does not - it has no answer of
  // its own to store, and the request it refreshes for runs right after. This is the "on app start"
  // half (utils/authUtils.refreshRoles).
  useEffect(() => {
    if (getRole()) refreshRoles();
  }, []);

  const openModal = (eventInfo) => {
    // Attending is login-gated: an anonymous visitor is sent to sign up
    // (or log in) instead of seeing the registration modal. The event they
    // meant to register for travels along as the ?eventId= query param -
    // SelfSignup/AttendeeLogin pick it up from there, so it completes
    // automatically once they're authenticated.
    if (!getRole()) {
      navigate(eventInfo?.id ? `/signup?eventId=${eventInfo.id}` : '/signup');
      return;
    }
    setSelectedEvent(eventInfo || {});
    setModalOpen(true);
    setRegistrationSuccess(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    setRegistrationSuccess(false);
  };

  const handleRegistrationSuccess = () => {
    setRegistrationSuccess(true);
    setTimeout(() => {
      closeModal();
    }, 3000);
  };

  const getContent = (defaultContent, enContent) => {
    return language === 'en' && enContent ? enContent : (defaultContent || '');
  };

  return (
    <IntlProvider messages={messages[language]} locale={language}>
      <AppWrapper>
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={
            registrationSuccess
              ? <FormattedMessage id="registration.success" defaultMessage="Registration Successful" />
              : <>{getContent(selectedEvent?.mainHeadline, selectedEvent?.mainHeadlineEN) || ''}</>
            }
        >
          <AttendeeEventRegisterConfirm
            onSubmitSuccess={handleRegistrationSuccess}
            eventInfo={selectedEvent || {}}
            registrationSuccess={registrationSuccess}
          />
        </Modal>
        <Header />
        <ScrollToTop />
        <ContentWrapper>
          <Routes>
            <Route path="/" element={<Home openRegistrationModal={openModal} />} />
            {/* The public event page carries the program section itself (PublicProgramSection, in
                place of the old PROGRAM button and speaker grid); there is no separate preview
                route any more (plan §7.6 decision 1). */}
            <Route path="/events/:slug" element={<EventDetail openRegistrationModal={openModal} />} />
            <Route path="/people/:personId" element={<PublicProfilePage />} />
            {/* The print copy of the published program, opened by the server's PDF renderer to make
                the public program PDF (pages/ProgramPrintPage.js). */}
            <Route path="/events/:slug/program/print" element={<ProgramPrintPage />} />
            {/* The admin Program screen (plan §7.7), on the real admin API. The deep link
                /admin/program?event={eventId}&spot={assignmentId} selects the event and opens the
                spot. It replaces Speaker Management and carries the invite-reminder template. */}
            <Route path="/admin/program" element={<AdminRoute><ProgramAdmin /></AdminRoute>} />
            {/* The admin person screens (plan §7.7 items 10-11): one list replacing User Management
                and the speaker list, and the person detail with sources, change sets and research. */}
            <Route path="/admin/people" element={<AdminRoute><PersonList /></AdminRoute>} />
            <Route path="/admin/people/:id" element={<AdminRoute><PersonDetail /></AdminRoute>} />
            {/* Who works in the admin area (admins only; the screen's API refuses the team). */}
            <Route path="/admin/team" element={<AdminRoute><TeamManagement /></AdminRoute>} />
            {/* The admin E-Mails screen: the log of what the system sent (admins and the team). */}
            <Route path="/admin/emails" element={<AdminRoute><EmailAdminScreen /></AdminRoute>} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/content-management" element={<AdminRoute><ContentManagement /></AdminRoute>} />
            <Route path="/admin/submission-management" element={<AdminRoute><SubmissionList /></AdminRoute>} />
            <Route path="/login" element={<AttendeeLogin />} />
            {/* Public signup and the invitation-link landing (signup.PublicRoutes); the invitation
                emails' links point at /speaker/invite?token=... */}
            <Route path="/signup" element={<AttendeeSignup />} />
            {/* Its own route so the finished flow has a URL to land on - the
                destination Marketing configures for URL-based conversions (see
                SignupSuccess). Public on purpose: a direct visit or a reload
                just shows the same confirmation without the event name. */}
            <Route path="/signup/success" element={<SignupSuccess />} />
            <Route path="/speaker/invite" element={<SpeakerInvitationLanding />} />
            <Route path="/speaker/invite/respond" element={<MemberRoute><SpeakerInviteRespond /></MemberRoute>} />
            {/* The profile review a new speaker lands on after opening an invitation link */}
            <Route path="/speaker/invite/confirmed" element={<MemberRoute><SpeakerInviteConfirmation /></MemberRoute>} />
            {/* The former Speaker Hub. Its content (the person's invitations and their talk materials) is
                the profile's speaker area now; the path stays for the links in already sent invitation and
                reminder emails and redirects there, still speaker-only. */}
            <Route path="/speaker/dashboard" element={<SpeakerRoute><Navigate to="/profile#referentenbereich" replace /></SpeakerRoute>} />
            {/* The link in the "confirm your email" mail (account.EmailVerificationController) */}
            <Route path="/verify-email" element={<VerifyEmail />} />
            {/* MEMBER (every account) is exactly the access submissions need */}
            <Route path="/profile" element={<MemberRoute><AttendeeProfile /></MemberRoute>} />
            <Route path="/call-for-abstracts" element={<MemberRoute><EnglishOnly><CallForAbstracts /></EnglishOnly></MemberRoute>} />
            <Route path="/submissions" element={<MemberRoute><EnglishOnly><SubmissionsPage /></EnglishOnly></MemberRoute>} />
            <Route path="/uploads/*" element={null} />
            <Route path="/impressum" element={<ImpressumDisplay />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Routes>
        </ContentWrapper>
        <Footer />
      </AppWrapper>
    </IntlProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ConferenceProvider>
        <Router>
          <MatomoTracker />
          <AnalyticsTracker />
          <CookieConsent />
          <AppContent />
        </Router>
      </ConferenceProvider>
    </LanguageProvider>
  );
}

export default App;
